import { Component, Inject, InjectionToken, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { UserHeader } from '../../../models/user';
import { Diary } from '../../../models/diary';
import { DiaryState } from '../../../state/diaries/diary-state';
import { Diaries } from '../../../state/diaries/diary-actions';
import { UserService } from '../../../services/user.service'
import { Observable, shareReplay, Subject, takeUntil } from 'rxjs';
import { TableModule } from 'primeng/table';
import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { EditorModule } from 'primeng/editor';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TagModule } from 'primeng/tag';
import { AccordionModule } from 'primeng/accordion';
import { TabViewModule } from 'primeng/tabview';
import { SkeletonModule } from 'primeng/skeleton';
import { IDiaryManagerFactory, IDiaryManagerItem } from './diary-manager-item';
import { ActivatedRoute } from '@angular/router';
import { SYSTEM_USER_ID } from '../../../services/injection-tokens';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ConfirmationService } from 'primeng/api';

export const DIARY_ITEM_FACTORY = new InjectionToken<string>('Diary_Item_Factory');

@Component({
  selector: 'app-diary-manager',
  standalone: true,
  imports: [TableModule, AsyncPipe, DatePipe, EditorModule, FormsModule, ButtonModule,
    InputTextareaModule, CalendarModule, DropdownModule, FloatLabelModule,
    OverlayPanelModule, TagModule, AccordionModule, NgIf, TabViewModule,
    SkeletonModule, TooltipModule, ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './diary-manager.component.html',
  styleUrl: './diary-manager.component.scss'
})
export class DiaryManagerComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  diaries$: Observable<Diary[]> = this._store.select(DiaryState.openDiaries);
  closedDiaries$: Observable<Diary[]> = this._store.select(DiaryState.closedDiaries);

  diaryItems$: Observable<IDiaryManagerItem[]> | undefined
  closedDiaryItems$: Observable<IDiaryManagerItem[]> | undefined

  closedDiaries: Diary[] = [];

  activeUsers$: Promise<UserHeader[]> = this._user.getActiveUsers();
  now = new Date();

  dayRanges: number[] = [10, 30, 60, 90, 180, 365];

  dueDateLookaheadDays: number = 0;
  closedDateLookbackDays: number = 0;

  useMember = false;

  constructor
    (
      private _store: Store,
      private _user: UserService,
      private route: ActivatedRoute,
      private confirmationService: ConfirmationService,
      @Inject(DIARY_ITEM_FACTORY) private _diaryItemFactory: IDiaryManagerFactory,
      @Inject(SYSTEM_USER_ID) protected systemUserId: number
    ) {
    this.diaryItems$ = this._diaryItemFactory
      ?.createDiaryManagerItems(this.diaries$)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }), takeUntil(this._destroy$));

    this.closedDiaryItems$ = this._diaryItemFactory
      ?.createDiaryManagerItems(this.closedDiaries$)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }), takeUntil(this._destroy$));

    this._store.select(DiaryState.dueDateLookaheadDays)
      .pipe(takeUntil(this._destroy$))
      .subscribe(d => this.dueDateLookaheadDays = d)
    this._store.select(DiaryState.closedDateLookbackDays)
      .pipe(takeUntil(this._destroy$))
      .subscribe(d => this.closedDateLookbackDays = d)
  }

  ngOnInit(): void {
    this.refreshOpen();
    this.useMember = this.route.snapshot.queryParamMap.get('useMember') === 'true';
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  closeDiary(diaryItem: IDiaryManagerItem) {
    this.confirmationService.confirm({
      header: 'Close Diary',
      message: 'Are you sure you want to close this diary?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        const handler = diaryItem.getHandler();
        if (handler) {
          handler.closeDiary(diaryItem.getDiary());
        }
      }
    });
  }

  assignDiary(diaryItem: IDiaryManagerItem, user: UserHeader, dueDate: Date) {
    var handler = diaryItem.getHandler()

    if (handler)
      handler.assignDiary(diaryItem.getDiary(), user, dueDate);
  }

  onTableChange(event: any) {
    if (event.index === 0) {
      this._store.dispatch(new Diaries.RefreshOpenDiaries());
    }
    else if (event.index === 1) {
      this._store.dispatch(new Diaries.RefreshClosedDiaries());
    }
  }

  refreshClosed() {
    this._store.dispatch(new Diaries.DiaryManagerLookbackChanged({ lookback: this.closedDateLookbackDays }));
  }

  refreshOpen() {
    this._store.dispatch(new Diaries.DiaryManagerLookaheadChanged({ lookahead: this.dueDateLookaheadDays }));
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  isDueToday(dueDate: Date | null): boolean {
    if (!dueDate) return false;
    return this.isSameDay(new Date(dueDate), this.now);
  }

  isReassignDisabled(diaryItem: IDiaryManagerItem): boolean {
    const createdBy = diaryItem.getDiary().createdBy;
    return !createdBy || createdBy.id === this.systemUserId;
  }
}